import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export default function LoadingScreen({ onComplete }: { onComplete: () => void }) {
  const [count, setCount] = useState(0);
  const [wordIndex, setWordIndex] = useState(0);
  const words = ["Buduję", "Testuję", "Skaluję"];

  useEffect(() => {
    const wordInterval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 400);

    let startTimestamp: number | null = null;
    const duration = 1000;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * 100));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        onComplete();
      }
    };

    requestAnimationFrame(step);
    return () => clearInterval(wordInterval);
  }, [onComplete, words.length]);

  return (
    <motion.div
      className="fixed inset-0 z-[9999] bg-bg flex flex-col justify-between p-8 md:p-12"
      aria-hidden="true"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="text-xs text-muted uppercase tracking-[0.3em] animate-fade-in-up">
        DAIEN - Systemy AI
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={wordIndex}
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            exit={{ y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="text-4xl md:text-6xl lg:text-7xl font-display italic text-text-primary absolute"
          >
            {words[wordIndex]}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex flex-col items-end w-full animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <div className="text-6xl md:text-8xl lg:text-9xl font-display text-text-primary tabular-nums tracking-tighter mb-4">
          {String(count).padStart(3, "0")}
        </div>
        <div className="w-full h-[3px] bg-stroke/50 overflow-hidden relative">
          <div
            className="absolute top-0 left-0 bottom-0 accent-gradient transform origin-left"
            style={{
              width: '100%',
              transform: `scaleX(${count / 100})`,
              boxShadow: '0 0 8px hsl(var(--accent-from) / 0.35)',
              transition: 'transform 0.1s linear'
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}
